import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { environment } from "src/environments/environment.ipt.local";
import { AuthChannelMessage } from "./auth.models";

@Injectable({ providedIn: "root" })
export class AzureCrossTabService {
	private readonly channelName =
		`core-automatizaciones-auth-${environment.AZURE_CLIENT_ID}`;
	private readonly storageEventKey = `${this.channelName}-event`;
	private readonly tabId = this.getOrCreateTabId();
	private readonly changedSubject = new BehaviorSubject<boolean>(false);
	private channel: BroadcastChannel | null = null;

	readonly changed$ = this.changedSubject.asObservable();

	constructor() {
		this.initialize();
	}

	get currentTabId(): string {
		return this.tabId;
	}

	get hasChanged(): boolean {
		return this.changedSubject.value;
	}

	publish(
		type: AuthChannelMessage["type"],
		username: string | null
	): void {
		const message: AuthChannelMessage = {
			clientId: environment.AZURE_CLIENT_ID,
			sourceTabId: this.tabId,
			type,
			username,
		};

		if (this.channel) {
			this.channel.postMessage(message);
			return;
		}

		localStorage.setItem(
			this.storageEventKey,
			JSON.stringify(message)
		);
		localStorage.removeItem(this.storageEventKey);
	}

	handle(value: unknown): void {
		const message = value as Partial<AuthChannelMessage> | null;
		if (
			!message ||
			message.clientId !== environment.AZURE_CLIENT_ID ||
			message.sourceTabId === this.tabId ||
			(message.type !== "account-changed" &&
				message.type !== "session-cleared")
		) {
			return;
		}

		this.changedSubject.next(true);
	}

	private initialize(): void {
		if ("BroadcastChannel" in window) {
			this.channel = new BroadcastChannel(this.channelName);
			this.channel.addEventListener("message", (event) =>
				this.handle(event.data)
			);
		}

		window.addEventListener("storage", (event) => {
			if (
				event.key !== this.storageEventKey ||
				!event.newValue
			) {
				return;
			}

			try {
				this.handle(JSON.parse(event.newValue));
			} catch {
				return;
			}
		});
	}

	private getOrCreateTabId(): string {
		const key = "core-automatizaciones-tab-id";
		const storedId = sessionStorage.getItem(key);
		if (storedId) {
			return storedId;
		}

		const tabId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
		sessionStorage.setItem(key, tabId);
		return tabId;
	}
}
